import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdOrdersOrderIdIdTickets,
  getV1CompaniesCompanyIdOrdersOrderIdJobs,
  getV1CompaniesCompanyIdOrdersOrderIdLoads,
  GetV1CompaniesCompanyIdOrdersOrderIdLoadsData,
} from '@treadinc/horizon-api-spec';
import { noop } from 'lodash-es';
import { createContext, useCallback, useState } from 'react';

import { Job } from '~hooks/useJob/models';
import { Load, NON_CANCELLED_LOAD_STATES } from '~hooks/useLoad/models';
import { Order } from '~hooks/useOrders/models';
import { Ticket } from '~hooks/useTickets/models';
import { LoadsJobFilters } from '~types/loadsFilters';
import { fetchAllApiPages } from '~utils/fetchAllPages';

type LoadsTabQueryContextType = {
  addTicket: (ticket: Ticket) => void;
  clearQueryData: () => void;
  companyId: string;
  idsOfBusyLoads: string[];
  isFetchingJobs?: boolean;
  isFetchingLoads?: boolean;
  isFetchingTickets?: boolean;
  jobsData: Job[];
  loadsData: Load[];
  order: Order;
  refreshLoads: () => Promise<void>;
  refreshLoadsAndTickets: () => Promise<void>;
  removeStoredLoadById: (loadId: string) => void;
  removeStoredTicketsById: (ticketIds: string[]) => void;
  ticketsData: Ticket[];
  toggleLoadsBusyState: (loadsIds: string[]) => void;
  updateStoredJob: (job: Job) => void;
  updateStoredLoad: (load: Load) => void;
  updateStoredTicket: (ticket: Ticket) => void;
};

const LoadsTabQueryContext = createContext<LoadsTabQueryContextType>({
  addTicket: noop,
  clearQueryData: noop,
  companyId: '',
  idsOfBusyLoads: [],
  jobsData: [],
  loadsData: [],
  order: {} as Order,
  refreshLoads: async () => {},
  refreshLoadsAndTickets: async () => {},
  removeStoredLoadById: noop,
  removeStoredTicketsById: noop,
  ticketsData: [],
  toggleLoadsBusyState: noop,
  updateStoredJob: noop,
  updateStoredLoad: noop,
  updateStoredTicket: noop,
});

interface LoadsTabQueryContextProviderProps {
  companyId: string;
  filters?: LoadsJobFilters;
  order: Order;
}

function LoadsTabQueryContextProvider({
  children,
  companyId,
  filters,
  order,
}: React.PropsWithChildren<LoadsTabQueryContextProviderProps>) {
  const queryPrefix = 'LoadsTabQueryContext';
  const jobsQueryKey = `${queryPrefix}_jobs`;
  const loadsQueryKey = `${queryPrefix}_loads`;
  const ticketsQueryKey = `${queryPrefix}_tickets`;

  const queryClient = useQueryClient();

  const [idsOfBusyLoads, setIdsOfBusyLoads] = useState<string[]>([]);

  const loadsQuery = useQuery({
    queryKey: [loadsQueryKey, companyId, order.id, filters],
    queryFn: ({ signal }) => {
      const filterQuery: NonNullable<
        GetV1CompaniesCompanyIdOrdersOrderIdLoadsData['query']
      > = { 'filter[states]': NON_CANCELLED_LOAD_STATES };

      if (filters?.driverIds?.length) {
        filterQuery['filter[job][driver_ids]'] = filters.driverIds;
      }

      if (filters?.dropoffSiteIds?.length) {
        filterQuery['filter[job][dropoff_site_ids]'] = filters.dropoffSiteIds;
      }

      if (filters?.pickupSiteIds?.length) {
        filterQuery['filter[job][pickup_site_ids]'] = filters.pickupSiteIds;
      }

      if (filters?.states?.length) {
        filterQuery['filter[job][states]'] = filters.states;
      }

      if (filters?.assigned) {
        filterQuery['filter[job][assigned]'] = true;
      }

      if (filters?.vendorAccountIds?.length) {
        filterQuery['filter[job][vendor_account_ids]'] = filters.vendorAccountIds;
      }

      if (filters?.billingApproved !== undefined) {
        filterQuery['filter[billing_approved]'] = filters.billingApproved;
      }

      if (filters?.materialIds?.length) {
        filterQuery['filter[material_ids]'] = filters.materialIds;
      }

      if (filters?.ticketPotentialDuplicate !== undefined) {
        filterQuery['filter[ticket][potential_duplicate]'] =
          filters.ticketPotentialDuplicate;
      }

      return fetchAllApiPages(
        (query, pageSignal) =>
          getV1CompaniesCompanyIdOrdersOrderIdLoads({
            path: { 'company-id': companyId, 'order-id': order.id },
            signal: pageSignal,
            query: { ...filterQuery, ...query },
          }),
        Load.parse,
        { signal },
      );
    },
  });

  const ticketsQuery = useQuery({
    queryKey: [ticketsQueryKey, companyId, order.id],
    queryFn: ({ signal }) =>
      fetchAllApiPages(
        (query, pageSignal) =>
          getV1CompaniesCompanyIdOrdersOrderIdIdTickets({
            path: { 'company-id': companyId, 'order-id': order.id },
            signal: pageSignal,
            query,
          }),
        Ticket.parse,
        { signal },
      ),
  });

  const jobsQuery = useQuery({
    queryKey: [jobsQueryKey, companyId, order.id],
    queryFn: ({ signal }) =>
      fetchAllApiPages(
        (query, pageSignal) =>
          getV1CompaniesCompanyIdOrdersOrderIdJobs({
            path: { 'company-id': companyId, 'order-id': order.id },
            signal: pageSignal,
            query,
          }),
        Job.parse,
        { signal },
      ),
  });

  const updateStoredLoad = useCallback<LoadsTabQueryContextType['updateStoredLoad']>(
    (load) => {
      queryClient.setQueryData(
        [loadsQueryKey, companyId, order.id, filters],
        (oldLoads?: Load[]) => {
          return oldLoads?.map((oldLoad) => {
            if (oldLoad.id === load.id) {
              return load;
            }

            return oldLoad;
          });
        },
      );
    },
    [companyId, order.id, filters],
  );

  const addTicket = useCallback<LoadsTabQueryContextType['addTicket']>(
    (ticket) => {
      queryClient.setQueryData(
        [ticketsQueryKey, companyId, order.id],
        (oldTickets?: Ticket[]) => {
          return (oldTickets ?? []).concat([ticket]);
        },
      );
    },
    [companyId, order.id],
  );

  const updateStoredTicket = useCallback<LoadsTabQueryContextType['updateStoredTicket']>(
    (ticket) => {
      queryClient.setQueryData(
        [ticketsQueryKey, companyId, order.id],
        (oldTickets?: Ticket[]) => {
          return oldTickets?.map((oldTicket) => {
            if (oldTicket.id === ticket.id) {
              return ticket;
            }

            return oldTicket;
          });
        },
      );
    },
    [companyId, order.id],
  );

  const updateStoredJob = useCallback<LoadsTabQueryContextType['updateStoredJob']>(
    (job) => {
      queryClient.setQueryData([jobsQueryKey, companyId, order.id], (oldJobs?: Job[]) => {
        if (!oldJobs) return oldJobs;

        const jobExists = oldJobs.some((oldJob) => oldJob.id === job.id);
        if (!jobExists) return oldJobs;

        return oldJobs.map((oldJob) => {
          if (oldJob.id === job.id) {
            return job;
          }
          return oldJob;
        });
      });
    },
    [companyId, order.id],
  );

  const removeStoredLoadById = useCallback<
    LoadsTabQueryContextType['removeStoredLoadById']
  >(
    (loadId) => {
      queryClient.setQueryData(
        [loadsQueryKey, companyId, order.id, filters],
        (oldLoads?: Load[]) => {
          return oldLoads?.filter((oldLoad) => oldLoad.id !== loadId);
        },
      );
    },
    [companyId, order.id, filters],
  );

  const removeStoredTicketsById = useCallback<
    LoadsTabQueryContextType['removeStoredTicketsById']
  >(
    (ticketIds) => {
      queryClient.setQueryData(
        [ticketsQueryKey, companyId, order.id],
        (oldTickets?: Ticket[]) => {
          return oldTickets?.filter((oldTicket) => {
            return !ticketIds.includes(oldTicket.id);
          });
        },
      );
    },
    [companyId, order.id],
  );

  const clearQueryData = useCallback(() => {
    queryClient.removeQueries({
      queryKey: [loadsQueryKey, companyId, order.id],
    });
    queryClient.removeQueries({ queryKey: [ticketsQueryKey, companyId, order.id] });
    queryClient.removeQueries({ queryKey: [jobsQueryKey, companyId, order.id] });
  }, [companyId, order.id, jobsQueryKey, loadsQueryKey, queryClient, ticketsQueryKey]);

  const refreshLoads = useCallback(async () => {
    await loadsQuery.refetch();
  }, [loadsQuery.refetch]);

  const refreshLoadsAndTickets = useCallback(async () => {
    await Promise.all([loadsQuery.refetch(), ticketsQuery.refetch()]);
  }, [loadsQuery.refetch, ticketsQuery.refetch]);

  const loadsData = loadsQuery.data ?? [];
  const isFetchingLoads = loadsQuery.isFetching && !loadsQuery.isRefetching;

  const ticketsData = ticketsQuery.data ?? [];
  const isFetchingTickets = ticketsQuery.isFetching && !ticketsQuery.isRefetching;

  const jobsData = jobsQuery.data ?? [];
  const isFetchingJobs = jobsQuery.isFetching && !jobsQuery.isRefetching;

  const toggleLoadsBusyState = useCallback<
    LoadsTabQueryContextType['toggleLoadsBusyState']
  >((loadIds: string[]) => {
    setIdsOfBusyLoads((state) => {
      const newState = [...state];

      loadIds.forEach((loadId) => {
        const index = newState.indexOf(loadId);
        const isBusy = index > -1;

        if (isBusy) {
          newState.splice(index, 1);
        } else {
          newState.push(loadId);
        }
      });

      return newState;
    });
  }, []);

  return (
    <LoadsTabQueryContext.Provider
      value={{
        addTicket,
        clearQueryData,
        companyId,
        idsOfBusyLoads,
        isFetchingJobs,
        isFetchingLoads,
        isFetchingTickets,
        jobsData,
        loadsData,
        order,
        refreshLoads,
        refreshLoadsAndTickets,
        removeStoredLoadById,
        removeStoredTicketsById,
        ticketsData,
        toggleLoadsBusyState,
        updateStoredJob,
        updateStoredLoad,
        updateStoredTicket,
      }}
    >
      {children}
    </LoadsTabQueryContext.Provider>
  );
}

export default LoadsTabQueryContext;
export { LoadsTabQueryContextProvider };
